import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import adminService from "../../api/services/adminService";
import { confirmDanger } from "../../utils/confirmDialog";
import { FormSkeleton } from "../common/skeletons";

const inputClass =
  "w-full p-2.5 text-xs md:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white text-gray-700 min-w-0";

function displayName(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  return name || user?.email || user?.contactNo || `User #${user?.id}`;
}

function formatBoostEnd(endAt) {
  if (!endAt) return null;
  return new Date(endAt).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function GrantBoostSection({ onSuccess }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [grantMode, setGrantMode] = useState("package");
  const [packageKey, setPackageKey] = useState("");
  const [durationHours, setDurationHours] = useState("1");
  const [durationMinutes, setDurationMinutes] = useState("0");
  const [boostStatus, setBoostStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPackagesLoading(true);
      try {
        const res = await adminService.getBoostPackages();
        const list = res?.packages || [];
        if (!cancelled) {
          setPackages(list);
          if (list.length > 0) setPackageKey(list[0].key);
        }
      } catch {
        if (!cancelled) setPackages([]);
      } finally {
        if (!cancelled) setPackagesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim() || selectedUser) {
      setSearchResults([]);
      setShowDropdown(false);
      return undefined;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await adminService.searchUsersForSubscription(searchQuery.trim());
        setSearchResults(res?.users || []);
        setShowDropdown(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, selectedUser]);

  useEffect(() => {
    const userId = selectedUser?.id;
    if (!userId) {
      setBoostStatus(null);
      return undefined;
    }
    let cancelled = false;
    setStatusLoading(true);
    adminService
      .getUserBoostStatus(userId)
      .then((res) => {
        if (!cancelled) setBoostStatus(res);
      })
      .catch(() => {
        if (!cancelled) setBoostStatus(null);
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUser?.id]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery(displayName(user));
    setShowDropdown(false);
    setSearchResults([]);
  };

  const handleClearUser = () => {
    setSelectedUser(null);
    setSearchQuery("");
    setSearchResults([]);
    setBoostStatus(null);
  };

  const resolveDurationMinutes = () => {
    const h = Math.max(0, parseInt(String(durationHours), 10) || 0);
    const m = Math.max(0, parseInt(String(durationMinutes), 10) || 0);
    return h * 60 + m;
  };

  const handleGrant = async () => {
    const userId = selectedUser?.id;
    if (!userId) {
      toast.error("Select a user first");
      return;
    }

    let payload;
    let confirmLabel;
    if (grantMode === "package") {
      if (!packageKey) {
        toast.error("Select a boost package");
        return;
      }
      const pkg = packages.find((p) => p.key === packageKey);
      payload = { package_key: packageKey };
      confirmLabel = pkg?.isLifetime ? "Lifetime" : pkg?.label || packageKey;
    } else {
      const totalMinutes = resolveDurationMinutes();
      if (totalMinutes <= 0) {
        toast.error("Enter a valid duration");
        return;
      }
      payload = { duration_minutes: totalMinutes };
      const h = Math.floor(totalMinutes / 60);
      const rem = totalMinutes % 60;
      confirmLabel =
        h > 0 && rem > 0
          ? `${h}h ${rem}m`
          : h > 0
            ? `${h} hour${h === 1 ? "" : "s"}`
            : `${rem} minute${rem === 1 ? "" : "s"}`;
    }

    const ok = await confirmDanger(
      `Grant profile boost (${confirmLabel}) to ${displayName(selectedUser)} (ID ${userId})?` +
        (boostStatus?.hasActiveBoost
          ? " They already have an active boost — time will be added."
          : ""),
      "Grant profile boost?"
    );
    if (!ok) return;

    setSubmitting(true);
    try {
      const res = await adminService.grantUserBoost(userId, payload);
      if (res?.success) {
        toast.success(res?.message || "Boost granted");
        const statusRes = await adminService.getUserBoostStatus(userId);
        setBoostStatus(statusRes);
        onSuccess?.();
      } else {
        toast.error(res?.message || "Failed to grant boost");
      }
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.message || "Failed to grant boost");
    } finally {
      setSubmitting(false);
    }
  };

  const activeEnd =
    boostStatus?.boost?.end_at_str ||
    boostStatus?.boost?.end_at ||
    null;

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden mb-6">
      <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-violet-50 to-white">
        <h2 className="text-lg sm:text-xl font-bold text-gray-800 tracking-tight">
          Grant profile boost
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Admin or Developer: boost any user profile using an app package or a custom duration.
        </p>
      </div>
      <div className="p-5 sm:p-6">
        <div className="space-y-4 max-w-2xl">
          <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Search user (name, email, phone, or ID)
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSelectedUser(null);
                setSearchQuery(e.target.value);
              }}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              placeholder="Type to search..."
              className={inputClass}
            />
            {selectedUser ? (
              <button
                type="button"
                onClick={handleClearUser}
                className="absolute right-2 top-9 text-gray-400 hover:text-red-600 text-sm"
              >
                Clear
              </button>
            ) : null}
            {searching ? <FormSkeleton fields={2} /> : null}
            {showDropdown && searchResults.length > 0 ? (
              <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                {searchResults.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    onClick={() => handleSelectUser(u)}
                  >
                    <span className="font-medium text-gray-900">{displayName(u)}</span>
                    <span className="block text-xs text-gray-500">
                      ID {u.id}
                      {u.email ? ` · ${u.email}` : ""}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {selectedUser ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <span className="font-semibold">{displayName(selectedUser)}</span>{" "}
              <span className="text-gray-400">(ID {selectedUser.id})</span>
              {statusLoading ? (
                <p className="text-xs text-gray-500 mt-1">Checking boost status…</p>
              ) : boostStatus?.hasActiveBoost ? (
                <p className="text-xs text-violet-700 mt-1 font-medium">
                  Active boost until {formatBoostEnd(activeEnd) || "—"}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">No active boost</p>
              )}
            </div>
          ) : null}

          <div>
            <span className="block text-sm font-semibold text-gray-700 mb-2">Duration</span>
            <div className="flex flex-wrap gap-4 mb-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="grantMode"
                  checked={grantMode === "package"}
                  onChange={() => setGrantMode("package")}
                />
                Existing package
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="grantMode"
                  checked={grantMode === "manual"}
                  onChange={() => setGrantMode("manual")}
                />
                Custom duration
              </label>
            </div>

            {grantMode === "package" ? (
              packagesLoading ? (
                <FormSkeleton fields={1} />
              ) : (
                <select
                  value={packageKey}
                  onChange={(e) => setPackageKey(e.target.value)}
                  className={inputClass}
                >
                  {packages.map((pkg) => (
                    <option key={pkg.key} value={pkg.key}>
                      {pkg.isLifetime
                        ? `${pkg.label}`
                        : `${pkg.label} (${pkg.duration_minutes} min)`}
                    </option>
                  ))}
                </select>
              )
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Hours</label>
                  <input
                    type="number"
                    min={0}
                    max={168}
                    value={durationHours}
                    onChange={(e) => setDurationHours(e.target.value)}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Minutes</label>
                  <input
                    type="number"
                    min={0}
                    max={59}
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    className={inputClass}
                  />
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            disabled={!selectedUser?.id || submitting || (grantMode === "package" && !packageKey)}
            onClick={handleGrant}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
          >
            {submitting ? "Granting…" : "Grant boost"}
          </button>
        </div>
      </div>
    </div>
  );
}
